import { Box, Drawer, List, ListItem, ListItemButton, ListItemText, Toolbar } from "@mui/material";
import { getFirestore } from "firebase/firestore";
import { t } from "i18next";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { REDIRECT_URL } from "../contexts/AppContext";
import { isAndroid } from "../helper/helper";
import { firebaseApp } from "../pages/login/firebaseInit";
import { ReactComponent as ChartIcon } from "../rsc/chart.svg";
import { colorDark, colorDarkBlue, colorGrey, colorPrimary, colorPrimaryPale, colorPrimaryStatic } from "../rsc/colors/colors";
import { ReactComponent as FruitIcon } from "../rsc/fruit.svg";
import { ReactComponent as CogIcon } from "../rsc/ic_cog.svg";
import { ReactComponent as ContractIcon } from "../rsc/ic_contract.svg";
import { ReactComponent as DashboardIcon } from "../rsc/ic_dashboard.svg";
import { ReactComponent as InvoiceIcon } from "../rsc/ic_invoice.svg";
import { ReactComponent as ReportIcon } from "../rsc/ic_report.svg";
import { ReactComponent as MoneyIcon } from "../rsc/money.svg";
import { ReactComponent as PalmsIcon } from "../rsc/palms.svg";
import GooglePlayStoreg from "../rsc/playstore.png";
import { AdminInfo, MenuState, NFMStore, setMenuState } from "../stores/store";
import { SwitchLang } from "./SwitchLang";


type MenuProps = {
    isAdminRef: AdminInfo
}

const { version } = require('../../package.json')

const MENU_WIDTH = 250

type MenuFirestore = {
    business_data: boolean
    purchase: boolean
    statement: boolean
    invoice: boolean
}

export function Menu(props: MenuProps) {

    const menuState: MenuState = useSelector((state: any) => state.menu.value)
    const db = getFirestore();

    const [menuFromStore, setMenuFromStore] = useState<MenuFirestore>(undefined)

    useEffect(() => {
        const collection = firebaseApp.firestore().collection('niagaEnterprise')
        const unsubscribe = collection.onSnapshot((changes) => {
            var doc = changes.docs.find(d => d.id === "flags").data()
            const menu: MenuFirestore = doc?.menu
            setMenuFromStore(menu)
        })
        return () => {
            unsubscribe()
        }
    }, [])

    type PageMenuType = {
        type: string,
        pageName: string,
        pagePath?: string,
        icon?: any,
        isVisible?: boolean
    }

    const pages: PageMenuType[] = [
        {
            type: "page",
            pageName: t("dashboard"),
            pagePath: "explorer/dashboard",
            icon: <DashboardIcon />,
            isVisible: props.isAdminRef.dashboardEnable
        },
        {
            type: "page",
            pageName: t("report"),
            pagePath: "explorer/dashboard/report",
            icon: <ReportIcon />,
            isVisible: props.isAdminRef.dashboardEnable
        },
        {
            type: "page",
            pageName: t("my_prices"),
            pagePath: "explorer/my_prices",
            icon: <PalmsIcon />,
            isVisible: props.isAdminRef.hasMill
        },
        {
            type: "page",
            pageName: t("today_delivery"),
            pagePath: "explorer/today_delivery",
            icon: <ContractIcon />,
            isVisible: props.isAdminRef.hasMill && menuFromStore?.purchase === true
        },
        {
            type: "page",
            pageName: t("purchase_report"),
            pagePath: "explorer/purchases",
            icon: <ContractIcon />,
            isVisible: props.isAdminRef.hasMill && menuFromStore?.purchase === true
        },
        {
            type: "page",
            pageName: t("business_data"),
            pagePath: "explorer/business",
            icon: <ChartIcon />,
            isVisible: props.isAdminRef.hasMill && menuFromStore?.business_data === true
        },
        {
            type: "page",
            pageName: t("statement"),
            pagePath: "explorer/statement",
            icon: <MoneyIcon />,
            isVisible: props.isAdminRef.hasMill && menuFromStore?.statement === true
        },
        {
            type: "page",
            pageName: t("invoices"),
            pagePath: "explorer/pempem_invoices",
            icon: <InvoiceIcon />,
            isVisible: props.isAdminRef.hasMill && menuFromStore?.invoice === true
        },
        {
            type: "page",
            pageName: t("special_prices"),
            pagePath: "explorer/special_prices",
            icon: <FruitIcon />,
            isVisible: props.isAdminRef.hasMill
        },
        {
            type: "page",
            pageName: t("mill_settings"),
            pagePath: `explorer/companies/company_detail`,
            icon: <CogIcon />,
            isVisible: props.isAdminRef.hasMill
        },

    ]

    const [sectionSelected, setSectionSelected] = useState<PageMenuType>(pages[0])

    const pathStore = useSelector((state: any) => state.path.value)

    var location = useLocation()

    const navigate = useNavigate()

    const onChangeRoute = (path: PageMenuType) => {
        setSectionSelected(path)
        navigate(path.pagePath)
    }

    useEffect(() => {
        if (pathStore) {
            onChangeRoute(pathStore)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathStore])

    useEffect(() => {
        let mediaQuery = window.matchMedia("(max-width:1024px)");

        const handleMediaQueryChange = (event) => {
            if (!event.matches) {
                NFMStore.dispatch(setMenuState({ isMenuOpen: !menuState.isMenuOpen, isMobile: false }))
            } else {
                NFMStore.dispatch(setMenuState({ isMenuOpen: menuState.isMenuOpen, isMobile: true }))
            }
        };

        handleMediaQueryChange(mediaQuery);
        mediaQuery.addEventListener('change', handleMediaQueryChange);

        return () => {
            mediaQuery.removeEventListener('change', handleMediaQueryChange);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Used to know the user page when reloading
    useEffect(() => {
        const path = location.pathname.substring(1, location.pathname.length)

        if (path !== sectionSelected?.pagePath) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            setSectionSelected(pages.find(p => p.pagePath === path))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location])

    function onMenuItemClick(item: PageMenuType) {
        onChangeRoute(item)
    }

    function setMenuOpen() {
        NFMStore.dispatch(setMenuState({ isMenuOpen: !menuState.isMenuOpen, isMobile: menuState.isMobile }))
    }

    return (
        <Drawer
            variant={menuState.isMobile ? "temporary" : "permanent"}
            onClose={() => setMenuOpen()}
            open={menuState.isMenuOpen}
            className="lightBgColor"
            sx={{
                width: MENU_WIDTH,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: MENU_WIDTH, boxSizing: 'border-box',
                    boxShadow: `rgba(0, 0, 0, 0.25) 0px 2px 5px -1px, rgba(0, 0, 0, 0.3) 0px 1px 3px -1px;`,
                    backgroundColor: 'var(--color-light)'
                }
            }}
        >
            <Toolbar />
            <Box sx={{ overflow: 'auto', flex: 1 }}>
                <List sx={{ paddingTop: 0 }}>
                    {pages.filter(p => p.isVisible !== false).map((page, index) => {
                        if (page.type === "page") {
                            return (
                                <ListItem key={page.pagePath} disablePadding>
                                    <ListItemButton sx={{
                                        '&.Mui-selected': {
                                            backgroundColor: colorPrimaryPale(),
                                            color: sectionSelected?.pagePath === page?.pagePath ? colorPrimary() : colorDarkBlue()
                                        }
                                    }} selected={sectionSelected?.pagePath === page?.pagePath} onClick={() => onMenuItemClick(page)}>
                                        {React.cloneElement(page.icon, { fill: sectionSelected?.pagePath === page?.pagePath ? colorPrimaryStatic() : colorDarkBlue(), width: "25px" })}
                                        <ListItemText style={{
                                            marginLeft: "8px",
                                            color: sectionSelected?.pagePath === page?.pagePath ? colorPrimaryStatic() : colorDarkBlue()
                                        }}
                                            primaryTypographyProps={{ fontWeight: 600 }} primary={page.pageName} />
                                    </ListItemButton>
                                </ListItem>
                            )

                        } else {
                            return (
                                <ListItem key={page.pagePath} disablePadding>
                                    <ListItemText style={{ marginLeft: "24px" }} primaryTypographyProps={{ fontWeight: 600, color: colorGrey() }} primary={page.pageName} />
                                </ListItem>
                            )
                        }
                    })}
                </List>

            </Box>
            <div>
                <div hidden={!menuState.isMobile} className="colorDark">
                    <SwitchLang color={colorDark()} />
                </div>
                <div className="pempem-flex-row-rev" style={{ paddingLeft: 10, paddingRight: 10 }}>
                    <p style={{ lineHeight: '28px' }}>{version}</p>
                    {
                        isAndroid() &&
                        <a href={REDIRECT_URL} style={{ textDecoration: 'none', paddingLeft: 10 }} className="pempem-flex-row">
                            <div
                                className='pempem-flex-row'
                                style={{
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    height: "60px",
                                    borderRadius: "8px",
                                    alignItems: "center",
                                }} >
                                <img alt="background" style={{ height: "40px" }} src={GooglePlayStoreg} />
                            </div>
                        </a>
                    }
                </div>
            </div>
        </Drawer>
    )
}